import { validationConstants } from "constants/formValidation";
import { orderTypeConstants } from "constants/order";
import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import {
  validateServiceDateInput,
  validateInputTime,
} from "pages/CreateOrder/utils/helpers";
import {
  handleChangeTimeWithMask,
  handleChangeDateWithMask,
} from "pages/CreateOrder/utils/mask";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useGetAllPatientsQuery } from "store/queries/patient";
import { IAccount } from "types/Account/Account";
import { IOrder } from "types/Order/Order";

import { StyledFieldGrid } from "../../CreateOrder.styled";
import { DuplicateOrderWarning } from "../DuplicateOrderWarning/DuplicateOrderWarning";
import { StepCard } from "../StepCard";
import { StepKey } from "../StepCard/helper";
import { useStepStatus } from "../StepCard/useStepStatus";

const orderTypeToLocationKey: Partial<Record<string, string>> = {
  [orderTypeConstants.BUNDLED]: "bundled",
  [orderTypeConstants.PATIENT_RESPONSIBILITY]: "patientResponsibility",
  [orderTypeConstants.ED]: "ed",
  [orderTypeConstants.BARIATRICS]: "bariatrics",
  [orderTypeConstants.GFE]: "gfe",
};

function isLocationEnabledForOrderType(
  orderType: string,
  locationEnabled?: Record<string, boolean>
): boolean {
  const key = orderTypeToLocationKey[orderType];
  return !!(key && locationEnabled?.[key]);
}

export interface IVisit {
  orderType: string;
  accountId?: string;
  dateOfService: string;
  timeOfService: string;
  accountNumber: number;
  outOfPocketReason: { id: string; name: string } | null;
  estimatedVisitCostForPatient: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  orderId: string | null;
  accountLocationId: string | null;
  notScheduledYet: boolean;
}

interface IVisitFormProps {
  skipDuplicateCheck?: boolean;
  account?: IAccount | null;
  isRedo?: boolean;
  isDraft?: boolean;
  order?: IOrder | null;
}

export const outOfPocketReasons = [
  {
    id: "",
    name: "You do not have an active insurance plan",
  },
  {
    id: "",
    name: "Your insurance does not cover this procedure",
  },
  {
    id: "",
    name: "You have not yet met your deductible",
  },
];

export function VisitForm({
  skipDuplicateCheck = false,
  account = null,
  isRedo = false,
  isDraft = false,
  order = null,
}: IVisitFormProps = {}) {
  const {
    watch,
    control,
    trigger,
    formState: { errors },
    setValue,
  } = useFormContext<IVisit>();

  const orderType = watch("orderType");
  const dateOfService = watch("dateOfService");
  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const dateOfBirth = watch("dateOfBirth");
  const currentOrderId = watch("orderId");
  const accountLocationId = watch("accountLocationId");
  const notScheduledYet = watch("notScheduledYet");

  const showLocationDropdown = useMemo(() => {
    if ((isRedo || isDraft) && order?.accountLocationId) {
      return true;
    }
    return (
      isLocationEnabledForOrderType(orderType, account?.locationEnabled) &&
      (account?.accountLocations?.length ?? 0) > 0
    );
  }, [
    orderType,
    account?.locationEnabled,
    account?.accountLocations,
    isRedo,
    isDraft,
    accountLocationId,
  ]);

  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();
  const { data: patientsData } = useGetAllPatientsQuery(
    {
      firstName,
      lastName,
      dateOfService,
      dateOfBirth,
      searchType: "exact",
    },
    {
      skip: !firstName || !lastName || !dateOfService || !dateOfBirth,
    }
  );

  // Find the first duplicate order that (unless we're skipping this check)
  // and exclude the current order if we're editing/reviewing one
  const duplicateOrder = useMemo(() => {
    if (skipDuplicateCheck || !patientsData?.data?.[0]?.orders?.length) {
      return null;
    }

    // Find the first order that's not the current order we're working on
    const firstDifferentOrder = patientsData.data[0].orders.find(
      (order: any) => order.id !== currentOrderId
    );

    return firstDifferentOrder || null;
  }, [skipDuplicateCheck, patientsData, currentOrderId]);

  const showOutOfPocketReason = orderType === orderTypeConstants.BUNDLED;

  useEffect(() => {
    if (showOutOfPocketReason) {
      setValue("outOfPocketReason", outOfPocketReasons[0]);
    } else {
      setValue("outOfPocketReason", null);
    }
  }, [showOutOfPocketReason]);

  useEffect(() => {
    if (!showLocationDropdown) {
      setValue("accountLocationId", null);
    }
  }, [showLocationDropdown, trigger]);

  useEffect(() => {
    if (notScheduledYet) {
      trigger("dateOfService");
      trigger("timeOfService");
    }
  }, [notScheduledYet, trigger]);

  const status = useStepStatus(StepKey.VISIT);

  const notScheduledToggle = (
    <div className="flex align-items-center">
      <Controller
        name="notScheduledYet"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Checkbox
            inputId="notScheduledYet"
            data-testid="notScheduledYet"
            onChange={(e) => {
              const checked = e.checked ?? false;
              onChange(checked);
              if (checked) {
                setValue("dateOfService", "01/01/2999" as any);
                setValue("timeOfService", null as any);
              } else {
                setValue("dateOfService", null as any);
                setValue("timeOfService", null as any);
              }
            }}
            checked={!!value}
          />
        )}
      />

      <label htmlFor="notScheduledYet" className="ml-2 white-space-nowrap">
        Not scheduled
      </label>
    </div>
  );

  return (
    <StepCard
      data-testid="visitTitle"
      status={status}
      icon={<i className="pi pi-calendar text-base" />}
      title="Visit"
      description="When the service takes place"
      action={notScheduledToggle}
    >
      <StyledFieldGrid className="flex flex-column gap-4 w-100">
        <div className="formgrid grid w-100">
          {!notScheduledYet && (
            <>
              <div className="field flex flex-column col-12 md:col-4 lg:col-3">
                <Label htmlFor="dateOfService" required>
                  Date of service
                </Label>

                <Controller
                  data-testid="dateOfService"
                  name="dateOfService"
                  control={control}
                  rules={{
                    validate: (date) =>
                      notScheduledYet ||
                      validateServiceDateInput(date, "any", orderType),
                    required: notScheduledYet
                      ? false
                      : validationConstants.VISIT_INFO_FORM.DATE_OF_SERVICE,
                  }}
                  render={({ field: { onChange, onBlur, value } }) => {
                    return (
                      <>
                        <InputText
                          id="dateOfService"
                          value={value || ""}
                          placeholder="MM/DD/YYYY"
                          onBlur={() => {
                            handleOnBlurField({
                              onBlur,
                              field: "dateOfService",
                              trigger,
                            });
                          }}
                          onChange={(e) =>
                            handleChangeDateWithMask(e, onChange)
                          }
                        />

                        {getFormErrorMessage("dateOfService", errors)}
                      </>
                    );
                  }}
                />
              </div>

              <div className="field flex flex-column col-12 md:col-4 lg:col-3">
                <Label htmlFor="timeOfService" optional>
                  Time of service
                </Label>

                <Controller
                  control={control}
                  name="timeOfService"
                  data-testid="timeOfService"
                  rules={{
                    validate: (time) =>
                      notScheduledYet || validateInputTime(time),
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <>
                      <InputText
                        id="timeOfService"
                        value={value || ""}
                        placeholder="HH:MM PM"
                        onBlur={() =>
                          handleOnBlurField({
                            onBlur,
                            field: "timeOfService",
                            trigger,
                          })
                        }
                        onChange={(e) => handleChangeTimeWithMask(e, onChange)}
                      />

                      {getFormErrorMessage("timeOfService", errors)}
                    </>
                  )}
                />
              </div>
            </>
          )}

          <div className="field flex flex-column col-12 md:col-4 lg:col-3">
            <Label htmlFor="accountNumber" required>
              Account / Visit ID
            </Label>

            <Controller
              name="accountNumber"
              control={control}
              rules={{
                required: validationConstants.VISIT_INFO_FORM.VISIT_ID,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <>
                  <InputText
                    data-testid="accountNumber"
                    id="accountNumber"
                    placeholder="Account ID"
                    autoComplete="off"
                    value={value || ""}
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "accountNumber",
                        trigger,
                      })
                    }
                    onChange={(e) => onChange(e.target.value)}
                  />

                  {getFormErrorMessage("accountNumber", errors)}
                </>
              )}
            />
          </div>

          {showOutOfPocketReason && (
            <div className="field flex-column col-12 md:col-4 lg:col-3 hidden">
              <Label htmlFor="outOfPocketReason">Out of pocket reason</Label>

              <Controller
                data-testid="outOfPocketReason"
                name="outOfPocketReason"
                control={control}
                rules={{
                  required:
                    validationConstants.VISIT_INFO_FORM.OUT_OF_POCKET_REASON,
                }}
                render={({ field: { onChange, value, onBlur } }) => (
                  <Dropdown
                    id="outOfPocketReason"
                    data-testid="outOfPocketReason"
                    value={value || ""}
                    onChange={onChange}
                    options={outOfPocketReasons}
                    optionLabel="name"
                    placeholder="Select reason"
                    className="w-full"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "outOfPocketReason",
                        trigger,
                      })
                    }
                  />
                )}
              />
            </div>
          )}
          {showLocationDropdown && (
            <div className="field flex-column col-12 md:col-4 lg:col-3">
              <Label htmlFor="accountLocationId">Location</Label>
              <Controller
                data-testid="accountLocationId"
                name="accountLocationId"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    key={value ?? "empty"}
                    id="accountLocationId"
                    data-testid="accountLocationId"
                    value={value ?? ""}
                    onChange={(e) => {
                      onChange(e.value ?? null);
                      trigger("accountLocationId");
                    }}
                    options={account?.accountLocations ?? []}
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Select location"
                    className="w-full"
                    showClear
                    filter
                    filterBy="name"
                    filterPlaceholder="Search by name"
                  />
                )}
              />
            </div>
          )}
        </div>
        {duplicateOrder && (
          <DuplicateOrderWarning orderId={duplicateOrder.id} />
        )}
      </StyledFieldGrid>
    </StepCard>
  );
}
